import { useMemo } from "react"
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../reusable/DropdownMenu"
import InfoTooltip from "../reusable/InfoTooltip"
import { Tooltip } from "../reusable/Tooltip"
import { formatNumber } from "~/utils/formatting"
import { formatName } from "~/utils/namingUtils"
import {
  syncToUnfulfillablePreorders,
  type PrimaryItem,
  type UnfulfillableSyncRow,
} from "~/utils/purchaseOrderSync"
import {
  getAtRiskSyncDescription,
  getUnfulfillableSyncDescription,
} from "~/utils/unfulfillablePreorderUtils"
import { sumBy } from "~/utils/tsUtils"

type Props = {
  primaryItems: PrimaryItem[]
  rowData: UnfulfillableSyncRow[]
  hasUnfulfillable: boolean
  hasUnfulfillableOrAtRisk: boolean
  onSync: (type: "unfulfillable" | "both") => void
}

/**
 * The "Cover shortfall" section of the PO sync-options dropdown: the two shortfall sync
 * options, each with a live preview of the resulting order total. Previews reuse the sync
 * function itself so they always match the applied outcome, and are only computed while
 * the menu is open (the dropdown content unmounts when closed).
 */
export default function ShortfallSyncMenuItems({
  primaryItems,
  rowData,
  hasUnfulfillable,
  hasUnfulfillableOrAtRisk,
  onSync,
}: Props) {
  const preview = useMemo(
    () => ({
      unfulfillable: sumBy(
        syncToUnfulfillablePreorders(primaryItems, rowData, "unfulfillable"),
        "quantity",
      ),
      both: sumBy(syncToUnfulfillablePreorders(primaryItems, rowData, "both"), "quantity"),
    }),
    [primaryItems, rowData],
  )

  const options = [
    {
      type: "unfulfillable" as const,
      enabled: hasUnfulfillable,
      disabledTooltip: `No unfulfillable ${formatName("preorder", true)} before delivery date`,
      label: "Confirmed shortfall only",
      subtitle: "Suggested order + unfulfillable",
      previewTotal: preview.unfulfillable,
      infoTitle: getUnfulfillableSyncDescription(),
    },
    {
      type: "both" as const,
      enabled: hasUnfulfillableOrAtRisk,
      disabledTooltip: `No unfulfillable or at-risk ${formatName("preorder", true)} before delivery date`,
      label: "Confirmed + projected shortfall",
      subtitle: "Suggested order + unfulfillable + at-risk",
      previewTotal: preview.both,
      infoTitle: getAtRiskSyncDescription(),
    },
  ]

  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuLabel>Cover {formatName("preorder")} shortfall</DropdownMenuLabel>
      {options.map((option) => (
        <Tooltip
          key={option.type}
          title={option.enabled ? "" : option.disabledTooltip}
          side="right"
        >
          <DropdownMenuItem onClick={() => onSync(option.type)} disabled={!option.enabled}>
            <div className="flex w-full flex-col gap-0.5">
              <div className="flex justify-between w-full items-center gap-2">
                {option.label}
                {option.enabled && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-fg-subtle tabular-nums">
                      → {formatNumber(option.previewTotal)}
                    </span>
                    <InfoTooltip className="h-4 w-4" title={option.infoTitle} />
                  </div>
                )}
              </div>
              <span className="text-xs text-fg-subtle">{option.subtitle}</span>
            </div>
          </DropdownMenuItem>
        </Tooltip>
      ))}
    </>
  )
}
